import {
  chartAxisX,
  chartHeight,
  measurementChartGroups,
  type Measurement,
  type MeasurementChart,
  type MeasurementChartGroup,
  type MeasurementChartGroupKey,
  type MeasurementType,
} from "@care-by-ella/contracts";

// TES-1481 (TES-1458 §5, Ö-805) — her measurements as the doctor sees them
// above the correction list: the same charts, in the same order, on the same
// scales as her "Grafer och trender". Emelie 2026-10-20:
// "Gemensamma grafgrupper och skalor används för patient och behörig personal."
//
// TES-1510 — and on the same proportional time axis, with blood pressure and
// resting pulse one group of two charts that share it, as in the app.
//
// Which values share a chart, which points are drawn and where they sit all
// come from contracts' measurementChartGroups and chartAxisX; this file only
// turns that into SVG coordinates and Swedish labels, so it is testable
// without rendering. The doctor's chart adds what the app leaves out (the
// scale's ends and the dates), never a different scale.

/** The chart titles, as the app names them. */
export const CHART_TITLE: Record<MeasurementType, string> = {
  weight: "Vikt",
  waist: "Midjemått",
  hip: "Höftmått",
  blood_pressure: "Blodtryck",
  pulse: "Vilopuls",
  height: "Längd",
};

/** The group titles, as the app names its cards. */
export const GROUP_TITLE: Record<MeasurementChartGroupKey, string> = {
  weight: "Vikt",
  waist: "Midjemått",
  blood_pressure_pulse: "Blodtryck och vilopuls",
  height: "Längd",
  hip: "Höftmått",
};

const UNIT: Record<MeasurementType, string> = {
  weight: "kg",
  waist: "cm",
  hip: "cm",
  blood_pressure: "mmHg",
  pulse: "slag/min",
  height: "cm",
};

export interface ChartFrame {
  width: number;
  height: number;
  padLeft: number;
  padRight: number;
  padTop: number;
  padBottom: number;
}

export interface DrawnPoint {
  id: string;
  x: number;
  y: number;
  /** "13 okt 2026 · 72,4 kg" — the dot's tooltip. */
  title: string;
}

export interface DrawnLine {
  key: string;
  muted: boolean;
  /** SVG polyline points; empty for a single reading. */
  polyline: string;
  points: DrawnPoint[];
}

export interface DrawnChart {
  type: MeasurementType;
  title: string;
  lines: DrawnLine[];
  /** The scale's ends, written at its top and bottom (one when flat). */
  scale: { label: string; y: number }[];
  /**
   * The first and last date of the group's axis, or one when every occasion
   * is the same day. The charts of a group share them.
   */
  dates: { label: string; x: number }[];
  /** Read by a screen reader in place of the picture. */
  summary: string;
}

export interface DrawnGroup {
  key: MeasurementChartGroupKey;
  title: string;
  /** In the group's order: blood pressure above pulse. */
  charts: DrawnChart[];
}

/** "72,4" — a decimal comma, as everywhere in the Swedish admin. */
export function formatChartValue(value: number): string {
  return String(value).replace(".", ",");
}

export function formatChartDate(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Stockholm",
  });
}

function withUnit(type: MeasurementType, value: number): string {
  return `${formatChartValue(value)} ${UNIT[type]}`;
}

function drawChart(
  chart: MeasurementChart,
  xOf: Map<string, number>,
  dates: DrawnChart["dates"],
  frame: ChartFrame,
): DrawnChart {
  const innerH = frame.height - frame.padTop - frame.padBottom;
  const yOf = (value: number) =>
    frame.padTop + (1 - chartHeight(chart.domain, value)) * innerH;
  const lines = chart.lines.map((line): DrawnLine => {
    const points = line.points.map((p) => ({
      id: p.id,
      x: xOf.get(p.key)!,
      y: yOf(p.value),
      title: `${formatChartDate(p.measuredAt)} · ${withUnit(chart.type, p.value)}`,
    }));
    return {
      key: line.key,
      muted: line.muted,
      polyline:
        points.length < 2 ? "" : points.map((p) => `${p.x},${p.y}`).join(" "),
      points,
    };
  });

  const { min, max } = chart.domain;
  const scale =
    min === max
      ? [{ label: withUnit(chart.type, min), y: yOf(min) }]
      : [
          { label: withUnit(chart.type, max), y: yOf(max) },
          { label: withUnit(chart.type, min), y: yOf(min) },
        ];

  const own = chart.lines[0]!.points;
  const first = formatChartDate(own[0]!.measuredAt);
  const last = formatChartDate(own[own.length - 1]!.measuredAt);
  const lastOf = (i: number) => {
    const points = chart.lines[i]!.points;
    return points[points.length - 1]!.value;
  };
  const latest =
    chart.type === "blood_pressure"
      ? `${lastOf(0)}/${lastOf(1)} mmHg`
      : withUnit(chart.type, lastOf(0));
  const summary =
    `${CHART_TITLE[chart.type]}: ${own.length} ` +
    `${own.length === 1 ? "mätning" : "mätningar"} ` +
    `${first === last ? last : `${first}–${last}`}, senast ${latest}.`;

  return {
    type: chart.type,
    title: CHART_TITLE[chart.type],
    lines,
    scale,
    dates,
    summary,
  };
}

function drawGroup(
  group: MeasurementChartGroup,
  frame: ChartFrame,
): DrawnGroup {
  const xOf = chartAxisX(group.axis, {
    width: frame.width,
    padLeft: frame.padLeft,
    padRight: frame.padRight,
    // Same-day readings a dot and a half apart, as in the app.
    sameDayGap: 14,
  });
  const first = group.axis[0]!;
  const last = group.axis[group.axis.length - 1]!;
  const firstDate = formatChartDate(first.measuredAt);
  const lastDate = formatChartDate(last.measuredAt);
  const dates =
    firstDate === lastDate
      ? [{ label: lastDate, x: xOf.get(last.key)! }]
      : [
          { label: firstDate, x: xOf.get(first.key)! },
          { label: lastDate, x: xOf.get(last.key)! },
        ];
  return {
    key: group.key,
    title: GROUP_TITLE[group.key],
    charts: group.charts.map((c) => drawChart(c, xOf, dates, frame)),
  };
}

/** Every chart group she has, in the app's order. */
export function measurementCharts(
  measurements: Measurement[],
  frame: ChartFrame,
): DrawnGroup[] {
  return measurementChartGroups(measurements).map((g) => drawGroup(g, frame));
}
